import React from "react";
import {
  Container,
  Avatar,
  Paper,
  Grid,
  Stack,
  Text,
  Group,
  RingProgress,
  Table,
  Progress,
  Badge,
  Divider,
  ThemeIcon,
} from "@mantine/core";
import {
  IconUser,
  IconBook,
  IconCheck,
  IconAlertCircle,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import { NonPaginatedResponse } from "../../Lib/responses";
import { Endpoints } from "../../Lib/endpoints";
import MegaLoader from "../MegaLoader";
import "./styles.scss";
import { AssessmentProps, AttemptProps, LessonProps } from "../../Lib/types";

interface StudentPerformanceComponentProps {
  studentID: string;
  courseID: string;
}

// Function to get grade and color based on score
const getGradeAndColor = (score: number) => {
  if (score >= 75)
    return {
      grade: "A",
      color: "green",
      gradient: "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
    };
  if (score >= 65)
    return {
      grade: "B",
      color: "blue",
      gradient: "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)",
    };
  if (score >= 55)
    return {
      grade: "C",
      color: "yellow",
      gradient: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
    };
  if (score >= 45)
    return {
      grade: "D",
      color: "orange",
      gradient: "linear-gradient(135deg, #fb923c 0%, #ea580c 100%)",
    };
  return {
    grade: "F",
    color: "red",
    gradient: "linear-gradient(135deg, #f87171 0%, #dc2626 100%)",
  };
};

const StudentPerformance: React.FC<StudentPerformanceComponentProps> = ({
  studentID,
  courseID,
}) => {
  const { data, isLoading, error } = usePerformRequest<
    NonPaginatedResponse<{
      attempts: AttemptProps[];
      assessments: AssessmentProps[];
      lessons: LessonProps[];
    }>
  >({
    method: Endpoints.GetStudentPerformanceInCourse(courseID, studentID).method,
    route: Endpoints.GetStudentPerformanceInCourse(courseID, studentID).route,
  });

  // Show loader while data is being fetched
  if (isLoading) {
    return (
      <Container size="lg" className="student-performance-container">
        <div
          style={{
            height: "400px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MegaLoader size={60} type="dots" />
        </div>
      </Container>
    );
  }

  // Compute stats
  const lessons = data?.data.lessons || [];
  const assessments = data?.data.assessments || [];
  const attempts = data?.data.attempts || [];
  const completedLessons = lessons.length; // Placeholder: mark all as completed
  const totalLessons = lessons.length;
  const totalAssessments = assessments.length;
  const avgScore = attempts.length
    ? Math.round(
        attempts.reduce((a, b) => a + (b.score || 0), 0) / attempts.length
      )
    : 0;
  const progressPercent = totalLessons
    ? Math.round((completedLessons / totalLessons) * 100)
    : 0;

  // Map assessment attempts by assessmentID
  const attemptMap = attempts.reduce((acc, attempt) => {
    acc[attempt.assessmentID] = attempt;
    return acc;
  }, {} as Record<string, AttemptProps>);

  return (
    <Container size="lg" className="student-performance-container">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper radius="lg" p="lg" shadow="md" className="student-overview-card">
          <Group align="center" gap="xl">
            <Avatar size={80} radius="xl" color="purple" src={null}>
              <IconUser size={40} />
            </Avatar>
            <Stack gap={0}>
              <Text size="xl" fw={700} c="purple">
                Student Name
              </Text>
              <Text size="sm" c="dimmed">
                ID: {studentID}
              </Text>
              <Group gap="md" mt="xs">
                <Badge color="purple" size="lg" variant="light">
                  Avg. Score: {avgScore}%
                </Badge>
                <Badge color="blue" size="lg" variant="light">
                  Lessons: {completedLessons}/{totalLessons}
                </Badge>
                <Badge color="teal" size="lg" variant="light">
                  Assessments: {totalAssessments}
                </Badge>
              </Group>
            </Stack>
            <RingProgress
              size={100}
              thickness={10}
              roundCaps
              sections={[{ value: progressPercent, color: "purple" }]}
              label={
                <Text ta="center" size="md" fw={700}>
                  {progressPercent}%
                </Text>
              }
              style={{ marginLeft: 40 }}
            />
          </Group>
        </Paper>
      </motion.div>
      <Divider my="xl" label="Lessons" labelPosition="center" />
      <Grid gutter="xl">
        {lessons.map((lesson) => (
          <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 3 }} key={lesson.id}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper radius="md" p="md" shadow="xs" className="lesson-card">
                <Group gap="xs">
                  <ThemeIcon
                    size={36}
                    radius="md"
                    color="purple"
                    variant="light"
                  >
                    <IconBook size={20} />
                  </ThemeIcon>
                  <Stack gap={0}>
                    <Text fw={600}>{lesson.title}</Text>
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {lesson.description}
                    </Text>
                  </Stack>
                </Group>
                <Group gap="xs" mt="sm">
                  <Badge color="green" leftSection={<IconCheck size={12} />}>
                    Completed
                  </Badge>
                  <Text size="xs" c="dimmed">
                    {new Date(lesson.createdAt).toLocaleDateString()}
                  </Text>
                </Group>
              </Paper>
            </motion.div>
          </Grid.Col>
        ))}
      </Grid>
      <Divider my="xl" label="Assessments" labelPosition="center" />
      <Paper radius="md" p="md" shadow="xs" className="assessment-table">
        <Table striped highlightOnHover withTableBorder>
          <thead>
            <tr>
              <th>Assessment</th>
              <th>Score</th>
              <th>Grade</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {assessments.map((assessment) => {
              const attempt = attemptMap[assessment.id];
              const gradeInfo = attempt
                ? getGradeAndColor(attempt.score)
                : null;
              return (
                <tr key={assessment.id}>
                  <td>{assessment.title}</td>
                  <td>
                    <Progress
                      value={attempt?.score || 0}
                      color={gradeInfo?.color || "gray"}
                      size="lg"
                      radius="xl"
                      style={{ minWidth: 80 }}
                    />
                    <Text size="sm" fw={600}>
                      {attempt?.score ?? "-"}%
                    </Text>
                  </td>
                  <td>
                    {gradeInfo ? (
                      <Badge
                        size="lg"
                        style={{
                          background: gradeInfo.gradient,
                          color: "white",
                          fontWeight: 700,
                          fontSize: "14px",
                        }}
                      >
                        {gradeInfo.grade}
                      </Badge>
                    ) : (
                      <Text size="sm" c="dimmed">
                        -
                      </Text>
                    )}
                  </td>
                  <td>
                    {attempt
                      ? new Date(attempt.date).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    {attempt ? (
                      <Badge
                        color={gradeInfo?.color || "gray"}
                        leftSection={<IconCheck size={12} />}
                        style={{
                          background: gradeInfo?.gradient,
                          color: "white",
                        }}
                      >
                        {gradeInfo?.grade === "A"
                          ? "Excellent"
                          : gradeInfo?.grade === "B"
                          ? "Good"
                          : gradeInfo?.grade === "C"
                          ? "Fair"
                          : gradeInfo?.grade === "D"
                          ? "Needs Work"
                          : "Failed"}
                      </Badge>
                    ) : (
                      <Badge
                        color="gray"
                        leftSection={<IconAlertCircle size={12} />}
                      >
                        Not Attempted
                      </Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Paper>
    </Container>
  );
};

export default StudentPerformance;
